import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, MapPin } from "lucide-react";
import { EVENTS, CATEGORIES } from "../data/events";

const EASE = [0.76, 0, 0.24, 1];

export const FeaturedEvents = ({ eventType, month, setEventType }) => {
  const filtered = useMemo(() => {
    let out = EVENTS;
    if (eventType && eventType !== "all")
      out = out.filter((e) => e.category === eventType);
    if (month && month !== "any") {
      out = out.filter((e) => e.dateISO.slice(5, 7) === month);
    }
    return out;
  }, [eventType, month]);

  return (
    <section
      id="events"
      data-testid="featured-events-section"
      className="relative bg-[color:var(--bt-bg)] pt-32 md:pt-52 pb-32 md:pb-56 px-6 md:px-10"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="grid md:grid-cols-12 gap-6 items-end mb-14 md:mb-20"
        >
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span
                data-testid="events-eyebrow"
                className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
              >
                Chapter II · The Upcoming Season
              </span>
            </div>
            <h2
              data-testid="events-heading"
              className="font-serif-display text-[9vw] md:text-[6.5vw] leading-[0.92] tracking-tight text-[color:var(--bt-ink)]"
            >
              The upcoming{" "}
              <span className="italic font-light text-[color:var(--bt-gold)]">
                season.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:pb-6">
            <p className="text-[color:var(--bt-ink-dim)] text-sm md:text-base leading-relaxed max-w-md">
              A curated selection from this year's ledger - charity balls,
              business ceremonies and the white-tie country calendar.
              Available to view; attend by invitation.
            </p>
          </div>
        </motion.div>

        {/* Category chips */}
        <div
          className="flex flex-wrap gap-3 mb-10 md:mb-14"
          data-testid="category-chips"
        >
          <ChipButton
            active={eventType === "all"}
            onClick={() => setEventType("all")}
            testId="chip-all"
            count={EVENTS.length}
          >
            All Evenings
          </ChipButton>
          {CATEGORIES.map((c) => (
            <ChipButton
              key={c.id}
              active={eventType === c.id}
              onClick={() => setEventType(c.id)}
              testId={`chip-${c.id}`}
              count={EVENTS.filter((e) => e.category === c.id).length}
            >
              {c.label}
            </ChipButton>
          ))}
        </div>

        {/* Bento grid */}
        <div
          data-testid="events-grid"
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 md:grid-flow-row-dense"
        >
          {filtered.map((event, i) => (
            <EventCard
              key={event.id}
              event={event}
              index={i}
              count={filtered.length}
            />
          ))}
          {filtered.length === 0 && (
            <div
              data-testid="events-empty"
              className="md:col-span-12 border border-[color:var(--bt-gold-line)] px-8 py-20 text-center"
            >
              <p className="font-serif-display italic text-2xl text-[color:var(--bt-ink)]">
                No evenings match your search.
              </p>
              <p className="text-sm text-[color:var(--bt-ink-dim)] mt-3">
                Try widening the month, or removing a filter.
              </p>
            </div>
          )}
        </div>

        {/* View all */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-16 flex items-center justify-center"
        >
          <a
            data-testid="events-view-all"
            href="#calendar"
            className="bt-btn-ghost"
          >
            View the Full Season <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const ChipButton = ({ active, onClick, children, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group relative px-5 py-2.5 border transition-all duration-300 ${
      active
        ? "border-[color:var(--bt-gold)] bg-[color:var(--bt-gold)] text-[color:var(--bt-bg)]"
        : "border-[color:var(--bt-gold-line)] text-[color:var(--bt-ink-dim)] hover:border-[color:var(--bt-gold)] hover:text-[color:var(--bt-ink)]"
    }`}
  >
    <span className="text-[11px] tracking-widest-brand uppercase font-medium">
      {children}
    </span>
    <span className="ml-2 text-[10px] opacity-60">({count})</span>
  </button>
);

const cardSpan = (event) => {
  if (event.span === "feature") return "md:col-span-8";
  return "md:col-span-4";
};

const cardHeight = (event) => {
  if (event.span === "feature") return "h-[62vh] md:h-[78vh]";
  return "h-[52vh] md:h-[62vh]";
};

const EventCard = ({ event, index }) => {
  const isExternal = Boolean(event.externalUrl);
  const isPlaceholder = Boolean(event.placeholder);
  const wrapperProps = isPlaceholder
    ? {
        role: "presentation",
        "aria-disabled": "true",
      }
    : isExternal
    ? {
        href: event.externalUrl,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : { to: `/event/${event.id}` };
  const Wrapper = isPlaceholder ? "div" : isExternal ? "a" : Link;
  const wrapperClasses = isPlaceholder
    ? "group relative block border border-[color:var(--bt-gold-line)] cursor-default select-none"
    : "group relative block border border-[color:var(--bt-gold-line)] hover:border-[color:var(--bt-gold)] transition-colors duration-500";

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 0.9,
        ease: EASE,
        delay: (index % 3) * 0.08,
      }}
      className={`${cardSpan(event)}`}
    >
      <Wrapper
        {...wrapperProps}
        data-testid={`event-card-${event.id}`}
        className={wrapperClasses}
      >
      <div
        className={`relative overflow-hidden ${cardHeight(event)} spotlight-frame`}
      >
        <img
          src={event.image}
          alt={event.title}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-[1400ms] ease-out ${
            isPlaceholder
              ? "grayscale-[35%] opacity-90"
              : "group-hover:scale-[1.06]"
          }`}
        />
        {/* Base scrim for legibility */}
        <div className="absolute inset-0 bg-[color:var(--bt-bg)]/30" />
        {/* Bottom-heavy gradient for text pop */}
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bt-bg)] via-[color:var(--bt-bg)]/60 to-[color:var(--bt-bg)]/10" />

        <CornerTicks />

        <div className="absolute top-5 left-5 right-5 flex items-center justify-between z-10">
          <span className="text-xs tracking-widest-brand uppercase font-medium text-[color:var(--bt-gold)] bg-[color:var(--bt-bg)]/70 backdrop-blur-md px-3 py-1.5 border border-[color:var(--bt-gold-line)]">
            {event.categoryLabel}
          </span>
          <span className="text-xs tracking-widest-brand uppercase font-medium text-[color:var(--bt-ink-dim)] flex items-center gap-1.5">
            {isPlaceholder ? (
              "Coming Soon"
            ) : isExternal ? (
              <>
                Partner Site <ExternalLink size={11} />
              </>
            ) : (
              `No. ${String(index + 1).padStart(3, "0")}`
            )}
          </span>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 z-10">
          <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm tracking-widest-brand uppercase font-medium text-[color:var(--bt-ink)]">
            <span>{event.date}</span>
            <span className="h-px w-4 bg-[color:var(--bt-gold-line)]" />
            <span className="flex items-center gap-1.5">
              <MapPin size={12} /> {event.location}
            </span>
          </div>
          <h3 className="mt-4 font-serif-display font-semibold text-4xl md:text-6xl leading-[0.98] tracking-tight text-[color:var(--bt-ink)] drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)]">
            {event.title}
          </h3>
          <p className="mt-4 text-[color:var(--bt-ink)]/90 text-base md:text-lg max-w-xl leading-relaxed">
            {event.tagline}
          </p>
          <div className="mt-6 flex items-center justify-between">
            {isPlaceholder ? (
              <>
                <span className="text-sm md:text-base tracking-widest-brand uppercase font-semibold text-[color:var(--bt-gold)]">
                  Coming Soon
                </span>
                <span className="text-sm tracking-widest-brand uppercase font-medium text-[color:var(--bt-ink-dim)]">
                  Event Information To Follow
                </span>
              </>
            ) : (
              <>
                <span className="text-sm md:text-base tracking-widest-brand uppercase font-semibold text-[color:var(--bt-gold)]">
                  {event.price}
                </span>
                <span className="flex items-center gap-2 text-sm tracking-widest-brand uppercase font-medium text-[color:var(--bt-ink)] group-hover:text-[color:var(--bt-gold)] transition-colors">
                  {isExternal ? "Visit Site" : "Enquire"}
                  <span className="w-8 h-px bg-current transition-all duration-500 group-hover:w-14" />
                  {isExternal ? <ExternalLink size={13} /> : <ArrowUpRight size={14} />}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      </Wrapper>
    </motion.div>
  );
};

const CornerTicks = () => (
  <>
    <span className="absolute top-3 left-3 w-3 h-px bg-[color:var(--bt-gold)]" />
    <span className="absolute top-3 left-3 h-3 w-px bg-[color:var(--bt-gold)]" />
    <span className="absolute top-3 right-3 w-3 h-px bg-[color:var(--bt-gold)]" />
    <span className="absolute top-3 right-3 h-3 w-px bg-[color:var(--bt-gold)]" />
    <span className="absolute bottom-3 left-3 w-3 h-px bg-[color:var(--bt-gold)]" />
    <span className="absolute bottom-3 left-3 h-3 w-px bg-[color:var(--bt-gold)]" />
    <span className="absolute bottom-3 right-3 w-3 h-px bg-[color:var(--bt-gold)]" />
    <span className="absolute bottom-3 right-3 h-3 w-px bg-[color:var(--bt-gold)]" />
  </>
);

export default FeaturedEvents;
